'use client'

import Link from 'next/link'
import styled from 'styled-components'

const HEADER_HEIGHT = '3.5rem'
const SIDEBAR_WIDTH = '15rem'

/** Below this width the sidebar becomes a drawer opened from the header (Radix `sm` starts at 768px). */
export const MOBILE = '@media (max-width: 767px)'

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh;
    background: var(--color-background);
`

export const StyledHeader = styled.header`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 1rem;
    height: ${HEADER_HEIGHT};
    padding: 0 1.25rem;
    background: var(--color-panel-solid);
    border-bottom: 1px solid var(--gray-a5);

    ${MOBILE} {
        gap: 0.5rem;
        padding: 0 0.75rem;
    }
`

/** Hamburger opening the sidebar drawer, only on phones. */
export const MenuButton = styled.span`
    display: none;

    ${MOBILE} {
        display: contents;
    }
`

/** Hidden on phones to keep the header on one line. */
export const HideOnMobile = styled.span`
    display: contents;

    ${MOBILE} {
        display: none;
    }
`

export const Brand = styled(Link)`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    text-decoration: none;
    color: inherit;
    min-width: 0;

    h1, .rt-Heading {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`

/** Square "IS" mark used in the header and on the auth pages. */
export const BrandMark = styled.span<{ $size?: string }>`
    display: grid;
    place-items: center;
    width: ${props => props.$size ?? '1.9rem'};
    height: ${props => props.$size ?? '1.9rem'};
    border-radius: var(--radius-3);
    background: linear-gradient(135deg, var(--accent-9), var(--accent-11));
    color: var(--accent-contrast);
    font-weight: 700;
    font-size: calc(${props => props.$size ?? '1.9rem'} * 0.42);
    letter-spacing: 0.02em;
    box-shadow: var(--shadow-2);
`

export const ContentInner = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
`

export const StyledSidebar = styled.nav<{ $open?: boolean }>`
    position: sticky;
    top: ${HEADER_HEIGHT};
    width: ${SIDEBAR_WIDTH};
    height: calc(100vh - ${HEADER_HEIGHT});
    flex-shrink: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1rem 0.75rem;
    background: var(--color-panel-solid);
    border-right: 1px solid var(--gray-a5);

    ${MOBILE} {
        position: fixed;
        left: 0;
        z-index: 20;
        width: min(${SIDEBAR_WIDTH}, 85vw);
        height: calc(100dvh - ${HEADER_HEIGHT});
        transform: translateX(${props => (props.$open ? '0' : '-100%')});
        visibility: ${props => (props.$open ? 'visible' : 'hidden')};
        transition: transform 200ms ease, visibility 200ms;
        box-shadow: ${props => (props.$open ? 'var(--shadow-5)' : 'none')};
    }
`

/** Dims the content behind the open drawer; a tap closes it. */
export const SidebarBackdrop = styled.div`
    display: none;

    ${MOBILE} {
        display: block;
        position: fixed;
        inset: ${HEADER_HEIGHT} 0 0 0;
        z-index: 19;
        background: var(--black-a8);
    }
`

export const NavSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
`

export const NavSectionLabel = styled.div`
    padding: 0 0.75rem 0.4rem;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--gray-a10);
`

export const NavLink = styled(Link)<{ $active: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius-3);
    text-decoration: none;

    ${MOBILE} {
        padding-block: 0.75rem;
    }

    font-size: var(--font-size-2);
    font-weight: ${props => (props.$active ? 600 : 500)};
    color: ${props => (props.$active ? 'var(--accent-11)' : 'var(--gray-a11)')};
    background: ${props => (props.$active ? 'var(--accent-a3)' : 'transparent')};
    transition: background 120ms, color 120ms;

    svg {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
        color: ${props => (props.$active ? 'var(--accent-10)' : 'var(--gray-a9)')};
    }

    &:hover {
        background: ${props => (props.$active ? 'var(--accent-a4)' : 'var(--gray-a3)')};
        color: ${props => (props.$active ? 'var(--accent-11)' : 'var(--gray-12)')};

        svg {
            color: ${props => (props.$active ? 'var(--accent-10)' : 'var(--gray-11)')};
        }
    }
`

export const Content = styled.main`
    flex: 1;
    min-width: 0;
    padding: 1.75rem 2rem 3rem;

    ${MOBILE} {
        padding: 1rem 0.75rem 2rem;
    }
`

/** Pages without the app layout (login, setting a password). */
export const CenteredPage = styled.main`
    min-height: 100vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 1rem;
    background:
        radial-gradient(60rem 30rem at 50% -10%, var(--accent-a4), transparent 70%),
        var(--color-background);
`
